import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { LANGUAGES, getLang, setLang, t, type Lang } from '@/lib/i18n';
import { useMe } from '@/lib/queries';
import { Button } from '@/components/ui';
import { Flag } from '@/components/LanguageSwitch';
import { cn } from '@/lib/format';

/**
 * Welcome / training video, shown once per user after sign-in.
 *
 * The videos come from /api/training (signed in only, range-streamed so seeking
 * works everywhere); the same files live in DAM_TRAINING/VIDEOS for anyone
 * browsing the library. The server's welcome.json lists which languages exist,
 * so a new language is a file drop, not a deploy. Bumping its `version` shows
 * the popup again to everyone, e.g. after a re-cut.
 *
 * Dismissal is stored on the user account (server side).
 */

type Manifest = { version: number; videos: Partial<Record<Lang, string>>; posters?: Partial<Record<Lang, string>>; seen: boolean };

export const OPEN_TRAINING = 'dam:open-training';

// Order of the flag row (as requested), not the alphabetical LANGUAGES order.
const FLAG_ORDER: Lang[] = ['en', 'de', 'fr', 'nl', 'es', 'it'];

// A language switch re-mounts the whole app (I18nRoot), popup included; this
// flag carries "keep the popup open" across that re-mount.
const KEEP_OPEN = 'dam-training-keep-open';


// One fetch per page load, shared by the popup and the sidebar link.
let manifestPromise: Promise<Manifest | null> | null = null;
function loadManifest() {
  manifestPromise ??= fetch('/api/training', { credentials: 'same-origin', cache: 'no-cache' })
    .then((r) => (r.ok ? (r.json() as Promise<Manifest>) : null))
    .then((m) => (m && Object.keys(m.videos ?? {}).length > 0 ? m : null))
    .catch(() => null);
  return manifestPromise;
}

export function useTrainingManifest() {
  const [manifest, setManifest] = useState<Manifest | null>(null);
  useEffect(() => {
    let live = true;
    void loadManifest().then((m) => live && setManifest(m));
    return () => {
      live = false;
    };
  }, []);
  return manifest;
}

export function TrainingPopup() {
  const { data: me } = useMe();
  const manifest = useTrainingManifest();
  const [open, setOpen] = useState(false);
  const [lang, setVideoLang] = useState<Lang>(getLang());

  // First visit: open on its own, in the UI language if there is a video for it.
  useEffect(() => {
    if (!manifest || !me) return;
    const ui = getLang();
    setVideoLang(manifest.videos[ui] ? ui : manifest.videos.en ? 'en' : (Object.keys(manifest.videos)[0] as Lang));
    try {
      const keep = sessionStorage.getItem(KEEP_OPEN);
      sessionStorage.removeItem(KEEP_OPEN);
      if (keep || !manifest.seen) setOpen(true);
    } catch {
      /* storage blocked: don't nag on every page load */
    }
  }, [manifest, me]);

  // The sidebar link reopens it.
  useEffect(() => {
    const reopen = () => setOpen(true);
    window.addEventListener(OPEN_TRAINING, reopen);
    return () => window.removeEventListener(OPEN_TRAINING, reopen);
  }, []);

  if (!open || !manifest || !me) return null;

  const close = () => {
    // Remembered on the account, so it does not come back on another device.
    // Also flip the cached copy, so a language switch (which re-mounts) keeps it closed.
    manifest.seen = true;
    void fetch('/api/training/seen', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'ecomstat-dam' },
      body: JSON.stringify({ version: manifest.version }),
    }).catch(() => {});
    setOpen(false);
  };
  const src = manifest.videos[lang];
  // Frame 0 of each video (the logo card), so there is never a black box before play.
  const poster = manifest.posters?.[lang];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true" aria-labelledby="training-title">
      <div className="w-full max-w-4xl overflow-hidden rounded-xl bg-white shadow-2xl dark:bg-gray-900">
        <div className="flex items-center gap-4 border-b border-gray-200 px-5 py-3 dark:border-white/10">
          <img src="/aicotone-logo.png" alt="AIcotone" className="h-10 w-auto shrink-0" />
          <div className="min-w-0 flex-1">
            <h2 id="training-title" className="text-base font-semibold text-strong">{t('training.title')}</h2>
            <p className="text-xs text-muted">{t('training.intro')}</p>
          </div>
          <button type="button" onClick={close} aria-label={t('training.close')} className="self-start rounded p-1 text-faint hover:bg-gray-100 hover:text-strong dark:hover:bg-white/10">
            <X className="size-4" />
          </button>
        </div>
        {/* One click per language. All six are shown so people see what is coming;
            the ones without a video yet are greyed out. */}
        <div className="flex flex-wrap items-center gap-1.5 border-b border-gray-200 px-5 py-2 dark:border-white/10" role="group" aria-label={t('training.language')}>
          {FLAG_ORDER.map((l) => {
            const has = Boolean(manifest.videos[l]);
            return (
              <button
                key={l}
                type="button"
                disabled={!has}
                onClick={() => {
                  // Picking a video language also switches the DAM itself, like the
                  // top-right switcher (remembered for this browser).
                  setVideoLang(l);
                  if (l !== getLang()) {
                    try {
                      sessionStorage.setItem(KEEP_OPEN, '1');
                    } catch {
                      /* ignore */
                    }
                    setLang(l);
                  }
                }}
                aria-pressed={l === lang}
                title={LANGUAGES[l]}
                className={cn(
                  'flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-semibold uppercase tracking-wide',
                  l === lang
                    ? 'border-brand-600 bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300'
                    : 'border-gray-200 text-body hover:bg-gray-100 dark:border-white/10 dark:hover:bg-white/10',
                  !has && 'cursor-not-allowed opacity-35 hover:bg-transparent',
                )}
              >
                <Flag lang={l} />
                {l}
              </button>
            );
          })}
        </div>
        {/* key: switching language swaps the source and starts it from the top */}
        <video key={src} src={src} poster={poster} controls autoPlay playsInline className="aspect-video w-full bg-white" />
        <div className="flex items-center gap-3 px-5 py-3">
          <p className="min-w-0 flex-1 text-xs text-muted">{t('training.later')}</p>
          <Button onClick={close}>{t('training.gotIt')}</Button>
        </div>
      </div>
    </div>
  );
}

/** Ask the shell's popup to open (sidebar link). */
export function openTraining() {
  window.dispatchEvent(new Event(OPEN_TRAINING));
}
